/* Les tableaux lisibles sur téléphone — 03/09/2026
   ══════════════════════════════════════════════════════════════

   Sur écran étroit, `mobile.css` transforme chaque ligne de tableau en
   fiche empilée, et affiche devant chaque valeur l'intitulé de sa
   colonne. Cet intitulé, le CSS le lit dans l'attribut `data-label` de
   la cellule — mais il faut bien que quelqu'un l'y écrive.

   C'est le rôle de ce composant : il parcourt les tableaux affichés,
   recopie le texte de chaque en-tête dans les cellules de sa colonne,
   et recommence quand le contenu change.

   ── Pourquoi ainsi, et pas en modifiant les tableaux ─────────────
   La console compte une quinzaine de tableaux répartis sur 3 300
   lignes. Ajouter `data-label` à la main sur chaque cellule aurait
   voulu dire des centaines de modifications, donc des centaines
   d'occasions de casser l'affichage sur ordinateur — exactement ce
   qu'on s'interdit. Ici, aucun tableau n'est touché : on ajoute un
   attribut après coup, et un attribut ne se voit pas.

   ── Pourquoi c'est sans risque vis-à-vis de React ────────────────
   On n'ajoute, ne déplace et ne supprime AUCUNE balise : on pose
   seulement un attribut que React ne connaît pas et ne surveille donc
   pas. Il ne l'effacera pas au rendu suivant ; et s'il recrée la
   ligne, l'observateur repasse derrière.

   L'observateur écoute les ajouts et retraits de balises, jamais les
   changements d'attributs — sinon nos propres écritures le
   réveilleraient en boucle sans fin.

   ── Sur ordinateur ───────────────────────────────────────────────
   Le composant ne rend rien et ne fait rien : il sort immédiatement
   si l'écran est large. Aucun attribut n'est posé, aucun observateur
   n'est branché. */
import { useEffect } from 'react';
import { useMobile } from './ecran';

/* Un en-tête peut contenir autre chose que du texte (une icône, une
   case à cocher, un bouton de tri). On ne garde que le texte, réduit
   aux espaces simples, et on borne la longueur : un intitulé de trois
   lignes déséquilibrerait la fiche. */
function intitule(cellule) {
  const brut = (cellule.textContent || '').replace(/\s+/g, ' ').trim();
  return brut.length > 28 ? `${brut.slice(0, 27)}…` : brut;
}

function etiqueter(table) {
  // On prend la DERNIÈRE rangée d'en-tête : certains tableaux ont deux
  // niveaux (un titre groupé au-dessus des colonnes réelles), et c'est
  // la rangée du bas qui nomme vraiment chaque colonne.
  const rangees = table.querySelectorAll('thead tr');
  if (!rangees.length) return;
  const entetes = Array.from(rangees[rangees.length - 1].children).map(intitule);
  if (!entetes.length) return;

  // `:scope` limite aux lignes de CE tableau : sans lui, un tableau
  // imbriqué dans une cellule verrait ses lignes étiquetées avec les
  // en-têtes du tableau parent.
  const lignes = table.querySelectorAll(':scope > tbody > tr');
  lignes.forEach((ligne) => {
    Array.from(ligne.children).forEach((cellule, i) => {
      // Une cellule étalée sur plusieurs colonnes (« aucun résultat »)
      // n'appartient à aucune colonne en particulier : on la laisse
      // pleine largeur, sans intitulé.
      const etendue = cellule.getAttribute('colspan');
      const valeur = (etendue && Number(etendue) > 1) ? '' : (entetes[i] || '');
      if (cellule.getAttribute('data-label') !== valeur) {
        cellule.setAttribute('data-label', valeur);
      }
    });
  });
}

export default function TableauxMobiles() {
  const mobile = useMobile();

  useEffect(() => {
    if (!mobile || typeof document === 'undefined') return undefined;

    let enAttente = false;
    const passer = () => {
      enAttente = false;
      document.querySelectorAll('table').forEach(etiqueter);
    };
    /* On regroupe les rafales : un tableau qui reçoit cinquante lignes
       déclenche cinquante notifications, et on ne veut qu'un seul
       parcours, juste avant le prochain affichage. */
    const planifier = () => {
      if (enAttente) return;
      enAttente = true;
      window.requestAnimationFrame(passer);
    };

    passer();
    const observateur = new MutationObserver(planifier);
    observateur.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false,   // impératif : sinon nos écritures nous rappellent
    });
    return () => observateur.disconnect();
  }, [mobile]);

  return null;
}
